import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { t } from 'i18next';
import { computeImageHashFromDataUrl, computeFaceHash } from '../lib/imageHash';

// ---------- Helpers ----------
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function hammingDistance(a: string, b: string): number {
  let dist = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) dist++;
  }
  return dist;
}

// helper to resolve URLs
const API_BASE = 'https://api.isentinel360.com';
function resolveUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return `${API_BASE}/${url}`;
}

// ---------- Types ----------
interface StoreInfo {
  country_code: string;
  city_code: string;
}

interface UploadItem {
  file: File;
  preview: string;
  sex: 'M' | 'F' | 'V';
  threatLevel: 'High' | 'Medium' | 'Low';
  notes: string;
  hash?: string;
  duplicate?: any;
  uploadedPhotoUrl?: string;
}

interface DuplicateMatch {
  poiId: string;
  code: string;
  threatLevel: string;
  photoUrl: string;
  distance: number;
}

// ---------- Component ----------
export default function BulkPOIUpload({
  storeId,
  storeInfo,
  existingCodes,
  onSuccess,
  edgeIp,
  edgePort,
}: {
  storeId: string;
  storeInfo: StoreInfo;
  existingCodes: string[];
  onSuccess?: () => void;
  edgeIp?: string;
  edgePort?: number;
}) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [checking, setChecking] = useState(false);
  const [matchModal, setMatchModal] = useState<{
    itemIndex: number;
    matches: DuplicateMatch[];
    item: UploadItem;
  } | null>(null);

  const getNextNumber = () => {
    let max = 0;
    existingCodes.forEach(code => {
      const parts = code.split('-');
      if (parts.length >= 3) {
        const num = parseInt(parts[2], 10);
        if (!isNaN(num) && num > max) max = num;
      }
    });
    return max + 1;
  };

  const generateCode = (number: number, sex: string, threat: string) => {
    const threatNum = threat === 'High' ? 3 : threat === 'Medium' ? 2 : 1;
    return `${storeInfo.country_code}-${storeInfo.city_code}-${String(number).padStart(5, '0')}-${sex}-${threatNum}`;
  };

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const files = Array.from(e.target.files || []);
    const newItems: UploadItem[] = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      sex: 'M' as UploadItem['sex'],
      threatLevel: 'Low' as UploadItem['threatLevel'],
      notes: '',
    }));
    setItems(prev => [...prev, ...newItems]);
  };

  const updateItem = (index: number, field: keyof UploadItem, value: any) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Check for duplicates
  const checkDuplicates = async () => {
    setChecking(true);
    setError('');
    let foundIndex = -1;
    let foundMatches: DuplicateMatch[] = [];
    let foundItem: UploadItem | null = null;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // 1. Upload photo to VPS first (needed for embedding)
      let photoUrl = '';
      let embedding: number[] | null = null;
      try {
        const imageDataUrl = await fileToDataUrl(item.file);
        const uploadRes = await api.uploadPhoto(imageDataUrl);
        photoUrl = uploadRes.url;
        // ✅ STORE THE VPS URL IN THE ITEM - THIS IS CRITICAL FOR THE MODAL
        item.uploadedPhotoUrl = uploadRes.url;
        
        // 2. Compute embedding on VPS
        try {
          const embedRes = await api.post('/api/pois/compute-embedding', { photoUrl });
          if (embedRes.embedding && Array.isArray(embedRes.embedding)) {
            embedding = embedRes.embedding;
          }
        } catch (err) {
          console.warn('VPS embedding failed for', item.file.name, err);
        }
      } catch (err) {
        setError('Failed to process image for duplicate check');
        setChecking(false);
        return;
      }

      // 3. Check for duplicate using embedding
      if (embedding) {
        try {
          const res = await api.post('/api/duplicates/check-embedding', { embedding });
          if (res.duplicate && res.existing) {
            foundIndex = i;
            foundMatches = [{
              poiId: res.existing.id,
              code: res.existing.code,
              threatLevel: res.existing.threat_level || 'Unknown',
              photoUrl: res.existing.photo_url || '',
              distance: 0
            }];
            foundItem = item;
            break;
          }
        } catch (err: any) {
          setError(err.message);
          setChecking(false);
          return;
        }
      }
    }

    if (foundIndex >= 0 && foundItem) {
      setMatchModal({ itemIndex: foundIndex, matches: foundMatches, item: foundItem });
    } else {
      await handleUpload();
    }
    setChecking(false);
  };


  const handleSkipDuplicate = () => {
    setMatchModal(null);
    handleUpload();
  };

  const handleUseExisting = () => {
    setItems(prev => prev.filter((_, i) => i !== matchModal!.itemIndex));
    setMatchModal(null);
  };

  const handleUpload = async () => {
    setUploading(true);
    setError('');

    // 1. Fetch current codes from the server
    let codes: string[] = [];
    try {
      const data = await api.getStorePois(storeId);
      codes = data.map((p: any) => p.code);
    } catch (err) {
      codes = existingCodes;
    }

    // 2. Compute next number
    let max = 0;
    codes.forEach(code => {
      const parts = code.split('-');
      if (parts.length >= 3) {
        const num = parseInt(parts[2], 10);
        if (!isNaN(num) && num > max) max = num;
      }
    });
    let nextNum = max + 1;

    // 3. Upload each item
    for (let i = 0; i < items.length; i++) {
      setProgress({ current: i + 1, total: items.length });
      const item = items[i];

      let photoUrl = '';
      let faceHash: string | null = null;
      let faceEmbedding: number[] | null = null;

      try {
        const imageDataUrl = await fileToDataUrl(item.file);

        // Compute face hash (perceptual)
        try {
          faceHash = await computeFaceHash(imageDataUrl);
        } catch (err) {
          console.error('Face hash failed for', item.file.name, err);
        }

        // Upload photo to VPS first
        const uploadRes = await api.uploadPhoto(imageDataUrl);
        photoUrl = uploadRes.url;

        // Compute embedding on VPS
        try {
          const embedRes = await api.post('/api/pois/compute-embedding', { photoUrl });
          if (embedRes.embedding && Array.isArray(embedRes.embedding)) {
            faceEmbedding = embedRes.embedding;
          }
        } catch (err) {
          console.warn('VPS embedding failed for', item.file.name, err);
        }
      } catch (err: any) {
        setError(`Failed to process image: ${err.message}`);
        setUploading(false);
        return;
      }

      // Generate code with retry on duplicate
      let code = generateCode(nextNum, item.sex, item.threatLevel);
      let retry = true;
      let retryCount = 0;
      let maxRetries = 20; // Increased from 10 to give more room

      while (retry && retryCount < maxRetries) {
        try {
          await api.createPOI({
            code,
            threatLevel: item.threatLevel,
            photoUrl,
            notes: item.notes,
            storeId: storeId,
            photoHash: item.hash,
            //photoFaceHash: faceHash,
            faceEmbedding: faceEmbedding || undefined,
          } as any);
          retry = false;
          nextNum++;
          codes.push(code);
          // Add the new code to the local set so we don't try to reuse it
          codes.push(code);
        } catch (err: any) {
          // Check if it's a duplicate error
          const errorMsg = err.message || '';
          if (
            errorMsg.includes('Duplicate code') ||
            errorMsg.includes('duplicate key') ||
            errorMsg.includes('DUPLICATE_CODE') ||
            errorMsg.includes('409')
          ) {
            retryCount++;
            // Increment nextNum and generate a new code
            nextNum++;
            code = generateCode(nextNum, item.sex, item.threatLevel);
            console.log(`🔄 Retry ${retryCount}: trying code ${code}`);
            await new Promise(r => setTimeout(r, 200));
            continue;
          }
          // Any other error stops the batch
          setError(`Failed on "${code}": ${errorMsg}`);
          setUploading(false);
          return;
        }
      }

      if (retryCount >= maxRetries) {
        setError(`Failed to find a unique code after ${maxRetries} attempts`);
        setUploading(false);
        return;
      }
    }

    // 4. Success
    setUploading(false);
    setItems([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onSuccess) onSuccess();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => fileInputRef.current?.click()} type="button">
          {t('admin.selectImages')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFiles}
          className="hidden"
        />
        <span className="text-sm">{items.length} images selected</span>
      </div>

      {items.length > 0 && (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {items.map((item, idx) => (
            <div key={idx} className="border p-2 rounded flex gap-2 items-start">
              <img src={item.preview} className="w-16 h-16 object-cover rounded" />
              <div className="flex-1 space-y-1">
                <select value={item.sex} onChange={e => updateItem(idx, 'sex', e.target.value)} className="w-full p-1 border rounded text-sm">
                  <option value="M">{t('flag.sex.male')}</option>
                  <option value="F">{t('flag.sex.female')}</option>
                  <option value="V">{t('flag.sex.vehicle')}</option>
                </select>
                <select value={item.threatLevel} onChange={e => updateItem(idx, 'threatLevel', e.target.value)} className="w-full p-1 border rounded text-sm">
                  <option value="Low">{t('threat.low')}</option>
                  <option value="Medium">{t('threat.medium')}</option>
                  <option value="High">{t('threat.high')}</option>
                </select>
                <Input
                  placeholder={t('admin.notes')}
                  value={item.notes}
                  onChange={e => updateItem(idx, 'notes', e.target.value)}
                  className="text-sm"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}>✕</Button>
            </div>
          ))}
        </div>
      )}

      {uploading && (
        <div className="text-sm">
          Uploading {progress.current}/{progress.total}...
        </div>
      )}

      {error && <div className="text-red-600 text-sm">{error}</div>}

      <div className="flex gap-2">
        <Button onClick={checkDuplicates} disabled={items.length === 0 || checking || uploading} type="button">
          {checking ? t('duplicates.checkDuplicates') : t('duplicates.saveAll')}
        </Button>
      </div>

      {/* Duplicate Match Modal */}
      <Dialog open={!!matchModal} onOpenChange={() => setMatchModal(null)}>
        <DialogContent className="bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>{t('duplicates.possibleFound')}</DialogTitle>
          </DialogHeader>
            {matchModal && (
              <div className="space-y-4">
                <p>{t('duplicates.similarInNetwork')}</p>
                <div className="flex gap-4">
                  {/* New Image */}
                  <div className="border p-2 rounded">
                    <p className="text-xs font-semibold">{t('duplicates.uploadImage')}</p>
                    <img
                      src={matchModal.item.uploadedPhotoUrl || matchModal.item.preview}
                      className="w-20 h-20 object-cover"
                      onError={(e) => {
                        if (e.currentTarget.src !== matchModal.item.preview) {
                          e.currentTarget.src = matchModal.item.preview;
                        }
                      }}
                    />
                  </div>
                  {/* Existing POI Image */}
                  {matchModal.matches.map((m, idx) => (
                    <div key={idx} className="border p-2 rounded">
                      <p className="text-xs font-semibold">{t('duplicates.existing')} {m.code}</p>
                      {m.photoUrl ? (
                        <img
                          src={resolveUrl(m.photoUrl)}
                          className="w-20 h-20 object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="1"%3E%3Crect x="2" y="2" width="20" height="20" rx="2"%3E%3C/rect%3E%3Cpath d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5"%3E%3C/path%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div className="w-20 h-20 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                          No image
                        </div>
                      )}
                      <p className="text-xs">{t('duplicates.threat')}: {m.threatLevel || 'Unknown'}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleUseExisting}>
                    {t('duplicates.useExisting')}
                  </Button>
                  <Button variant="outline" onClick={handleSkipDuplicate}>
                    {t('duplicates.addAsNewEitherWay')}
                  </Button>
                </div>
              </div>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
}